import { useState } from 'react';
import { X, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface CreateInvoiceModalProps {
  fundCallId: string;
  fundCallName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateInvoiceModal({ fundCallId, fundCallName, onClose, onSuccess }: CreateInvoiceModalProps) {
  const [formData, setFormData] = useState({
    supplier: '',
    invoice_number: '',
    invoice_date: '',
    total_amount: '',
    description: '',
    allocated_amount: '',
    justification_note: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.supplier.trim()) {
      setError('Le fournisseur est obligatoire');
      return;
    }

    if (!formData.total_amount || parseFloat(formData.total_amount) <= 0) {
      setError('Le montant total doit être supérieur à 0');
      return;
    }

    if (!formData.allocated_amount || parseFloat(formData.allocated_amount) <= 0) {
      setError('Le montant affecté doit être supérieur à 0');
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Non authentifié');

      const totalAmount = parseFloat(formData.total_amount);
      const allocatedAmount = parseFloat(formData.allocated_amount);

      // Create invoice
      const { data: invoice, error: invoiceError } = await supabase
        .from('grant_invoices')
        .insert({
          user_id: user.id,
          supplier: formData.supplier.trim(),
          invoice_number: formData.invoice_number.trim() || null,
          invoice_date: formData.invoice_date || null,
          total_amount: totalAmount,
          description: formData.description.trim() || null,
          status: 'incomplet',
        })
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // Link to fund call
      const { error: linkError } = await supabase
        .from('grant_fund_call_invoices')
        .insert({
          user_id: user.id,
          fund_call_id: fundCallId,
          invoice_id: invoice.id,
          allocated_amount: allocatedAmount,
          justification_note: formData.justification_note.trim() || null,
        });

      if (linkError) throw linkError;

      onSuccess();
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const handleTotalAmountChange = (value: string) => {
    setFormData({ ...formData, total_amount: value });
    // Auto-fill allocated amount if empty
    if (!formData.allocated_amount && value) {
      setFormData({ ...formData, total_amount: value, allocated_amount: value });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Créer et lier une facture</h2>
            <p className="text-sm text-gray-600 mt-1">ADF: {fundCallName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fournisseur *
            </label>
            <input
              type="text"
              value={formData.supplier}
              onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Nom du fournisseur"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de facture
              </label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="N° facture"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de facture
              </label>
              <input
                type="date"
                value={formData.invoice_date}
                onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant total de la facture (€) *
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={formData.total_amount}
              onChange={(e) => handleTotalAmountChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="0.00"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Description de la facture..."
            />
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Liaison avec l'ADF</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Montant affecté de cet ADF (€) *
              </label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.allocated_amount}
                onChange={(e) => setFormData({ ...formData, allocated_amount: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="0.00"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Par défaut, égal au montant total si c'est le seul ADF lié
              </p>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note de justification
              </label>
              <textarea
                value={formData.justification_note}
                onChange={(e) => setFormData({ ...formData, justification_note: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Ex: Acompte 30%, Solde, etc."
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Création...' : 'Créer et lier'}
          </button>
        </div>
      </div>
    </div>
  );
}
